import React, { useEffect, useRef, useState } from "react";
import Styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";

const DEFAULT_CODE_LENGTH = 6;

const getErrorSource = (formErr) => {
    return (
        formErr?.response?.data?.errors ||
        formErr?.data?.errors ||
        formErr?.errors ||
        formErr ||
        {}
    );
};

const getFieldError = (formErr, fieldName) => {
    const errors = getErrorSource(formErr);
    const fieldError = errors?.[fieldName];

    if (!fieldError) return "";

    if (Array.isArray(fieldError)) {
        return fieldError[0] || "";
    }

    return String(fieldError);
};

const getCodeArray = (value, length) => {
    const normalizedValue = String(value || "")
        .replace(/\D/g, "")
        .slice(0, length);

    return Array.from({ length }, (_, index) => normalizedValue[index] || "");
};

const TwoFactorCodeInput = ({
                                value = "",
                                onChange,
                                formErr = null,
                                errorFieldName = "code",
                                isLoading = false,
                                length = DEFAULT_CODE_LENGTH,
                                label = "Код подтверждения",
                                hint = "Введите код, который сейчас отображается в приложении.",
                                autoFocus = false,
                                className = "",
                            }) => {
    const { t } = useTranslation();

    const inputsRef = useRef([]);

    const [isErrorHidden, setIsErrorHidden] = useState(false);

    const codeArray = getCodeArray(value, length);
    const fieldError = getFieldError(formErr, errorFieldName);
    const isErrorVisible = !!fieldError && !isErrorHidden && !isLoading;

    const updateValue = (nextCodeArray) => {
        const nextValue = nextCodeArray.join("");

        if (onChange) {
            onChange(nextValue);
        }
    };

    const hideError = () => {
        if (!isErrorHidden) {
            setIsErrorHidden(true);
        }
    };

    const codeChangeHandler = (event, index) => {
        const inputValue = event.target.value.replace(/\D/g, "");

        if (!inputValue) {
            const nextCodeArray = [...codeArray];

            nextCodeArray[index] = "";

            updateValue(nextCodeArray);
            hideError();

            return;
        }

        const pastedLikeValue = inputValue.slice(0, length);

        if (pastedLikeValue.length > 1) {
            const nextCodeArray = Array(length).fill("");

            pastedLikeValue.split("").forEach((digit, digitIndex) => {
                nextCodeArray[digitIndex] = digit;
            });

            updateValue(nextCodeArray);
            hideError();

            const nextFocusIndex = Math.min(pastedLikeValue.length, length - 1);

            inputsRef.current[nextFocusIndex]?.focus();

            return;
        }

        const nextCodeArray = [...codeArray];

        nextCodeArray[index] = pastedLikeValue;

        updateValue(nextCodeArray);
        hideError();

        if (pastedLikeValue && index < length - 1) {
            inputsRef.current[index + 1]?.focus();
        }
    };

    const codeKeyDownHandler = (event, index) => {
        if (event.key === "Backspace") {
            hideError();

            if (!codeArray[index] && index > 0) {
                inputsRef.current[index - 1]?.focus();
            }
        }

        if (event.key === "ArrowLeft" && index > 0) {
            inputsRef.current[index - 1]?.focus();
        }

        if (event.key === "ArrowRight" && index < length - 1) {
            inputsRef.current[index + 1]?.focus();
        }
    };

    const codePasteHandler = (event) => {
        event.preventDefault();

        const pastedValue = event.clipboardData
            .getData("text")
            .replace(/\D/g, "")
            .slice(0, length);

        if (!pastedValue) return;

        const nextCodeArray = Array(length).fill("");

        pastedValue.split("").forEach((digit, index) => {
            nextCodeArray[index] = digit;
        });

        updateValue(nextCodeArray);
        hideError();

        const nextFocusIndex = Math.min(pastedValue.length, length - 1);

        inputsRef.current[nextFocusIndex]?.focus();
    };

    useEffect(() => {
        if (formErr) {
            setIsErrorHidden(false);
        }
    }, [formErr]);

    useEffect(() => {
        if (autoFocus) {
            inputsRef.current[0]?.focus();
        }
    }, [autoFocus]);

    return (
        <div className={`${Styles.container} ${className}`}>
            {label && (
                <div className={Styles.label}>
                    {t(label)}
                </div>
            )}

            <div className={Styles.inputsWrap}>
                {codeArray.map((digit, index) => (
                    <input
                        key={index}
                        ref={(element) => {
                            inputsRef.current[index] = element;
                        }}
                        type="text"
                        value={digit}
                        onChange={(event) => codeChangeHandler(event, index)}
                        onKeyDown={(event) => codeKeyDownHandler(event, index)}
                        onPaste={codePasteHandler}
                        className={`
                            ${Styles.input}
                            ${isErrorVisible ? Styles.inputError : ""}
                        `}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={1}
                        disabled={isLoading}
                    />
                ))}
            </div>

            {isErrorVisible ? (
                <div className={Styles.error}>
                    {t(fieldError)}
                </div>
            ) : (
                hint && (
                    <div className={Styles.hint}>
                        {t(hint)}
                    </div>
                )
            )}
        </div>
    );
};

export default TwoFactorCodeInput;